import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  MoreOptions,
  Pagination,
  ProductLinkWithIcon
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { CompetenciesProductBadge } from 'components/competencies';
import {
  CalendarEventAchievementsAdd,
  CalendarEventAchievementsRemove
} from '.';

import type { GetCalendarEventAchievementsRes } from 'shared-types/responses';
import { AttributeDisplayValue } from 'components/common/customAttributes';

const CalendarEventAchievements = () => {
  const {
    language,
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, loading, updateData } =
    useApiData<GetCalendarEventAchievementsRes>({
      url: `/admin/calendar-events/${calendarEvent.id}/achievements?limit=10&page=${page}`,
      onlyIf: Boolean(calendarEvent?.id)
    });

  return (
    <>
      <Box
        headerContent={
          <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
            <CalendarEventAchievementsAdd updateAchievements={updateData} />

            <MoreOptions category="lighter">
              <CalendarEventAchievementsRemove
                updateAchievements={updateData}
              />
            </MoreOptions>
          </div>
        }
        loading={loading}
        padding={0}
        title={messages.eventCalendarLinkedAchievements}
        titleDescription={messages.eventCalendarAchievementTooltip}
      >
        {data?.achievements?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  {messages.achievement}
                </BoxTableCell>

                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  {messages.competenciesGranted}
                </BoxTableCell>

                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  {messages.commonCustomFields}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  shrink={true}
                  sticky={true}
                  stickyAnchor="right"
                />
              </BoxTableHeader>

              {data.achievements.map((achievement) => {
                const userAttributes = achievement.userAttributes?.filter(
                  (attribute) => attribute.value !== undefined
                );

                return (
                  <BoxTableRow key={achievement.id}>
                    <BoxTableCell cellBreakpoint={breakpoints.medium}>
                      <ProductLinkWithIcon
                        achievement={achievement}
                        disableLink={!achievement.isOwnerOrSuperadmin}
                      />
                    </BoxTableCell>

                    <BoxTableCell cellBreakpoint={breakpoints.medium}>
                      <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                        {achievement.competencies?.map((competency) => (
                          <CompetenciesProductBadge
                            key={`${competency.id}${
                              competency.level ? `-${competency.level.id}` : ''
                            }`}
                            competency={competency}
                          />
                        ))}
                      </FlexContainer>
                    </BoxTableCell>

                    <BoxTableCell cellBreakpoint={breakpoints.medium}>
                      {userAttributes?.length ? (
                        <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                          {userAttributes.map((attribute, attributeIndex) => (
                            <Badge
                              isRounded={false}
                              key={attributeIndex}
                              text={
                                <div>
                                  <span style={{ fontWeight: 400 }}>
                                    {attribute.titleTranslations?.[language] ||
                                      attribute.title}
                                    {': '}
                                  </span>
                                  <AttributeDisplayValue
                                    style={{ display: 'inline-block' }}
                                    value={attribute.value}
                                  />
                                </div>
                              }
                            />
                          ))}
                        </FlexContainer>
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell
                      cellBreakpoint={breakpoints.medium}
                      shrink={true}
                      sticky={true}
                      stickyAnchor="right"
                      stickyBackgroundColor="white"
                    >
                      <MoreOptions renderAsFixed={true}>
                        <CalendarEventAchievementsRemove
                          achievementId={achievement.id}
                          updateAchievements={updateData}
                        />
                      </MoreOptions>
                    </BoxTableCell>
                  </BoxTableRow>
                );
              })}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="check-circle"
            title={messages.achievementsEmpty}
          />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />
    </>
  );
};

export default CalendarEventAchievements;
